import { useState, useCallback } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

export type MatchTier = 'exact' | 'high' | 'mid' | 'unmatched';
// 確定済み履歴・見送り済み履歴の表示用タブを含む UI 用のタブキー
export type TabKey = MatchTier | 'committed' | 'skipped';

export type CustomerType = 'corporate' | 'individual';

/** 会員区分 (協力会費パターン 4,6 が正会員 / 5,7 が準会員) */
export type MembershipFilter = 'all' | 'member' | 'regular';

/** 詳細検索条件 (旧画面 accounting/tq_partner_list.js の検索パネル相当) */
export interface MatchSearchFilters {
  code_start: string;
  code_end: string;
  company_name: string;
  address: string;
  tel: string;
  fax: string;
  mobile: string;
  membership: MembershipFilter;
  hide_deleted: boolean;
  kana_sort: boolean;
  // 登録日 (ターコイズ MS_TORIHIKISAKI.MAKE_DATE) の範囲 'YYYY-MM-DD'。取引先一覧でのみ使用
  make_date_start: string;
  make_date_end: string;
}

export const EMPTY_FILTERS: MatchSearchFilters = {
  code_start: '',
  code_end: '',
  company_name: '',
  address: '',
  tel: '',
  fax: '',
  mobile: '',
  membership: 'all',
  hide_deleted: false,
  kana_sort: false,
  make_date_start: '',
  make_date_end: '',
};

/** 既定値から変更されている条件があるか (絞り込み中の表示判定に使う) */
export const hasActiveFilters = (f: MatchSearchFilters): boolean =>
  f.code_start !== '' || f.code_end !== '' || f.company_name !== '' || f.address !== '' ||
  f.tel !== '' || f.fax !== '' || f.mobile !== '' || f.membership !== 'all' ||
  f.hide_deleted || f.kana_sort || f.make_date_start !== '' || f.make_date_end !== '';

export interface MatchRow {
  match_id: number;
  customer_type: CustomerType;
  tier: MatchTier;
  torihikisaki_cd: string;
  torihikisaki_name: string | null;
  torihikisaki_kname: string | null;
  torihikisaki_kbn: string | null;
  torihikisaki_tel: string | null;
  torihikisaki_keitai_tel: string | null;
  torihikisaki_fax: string | null;
  torihikisaki_email: string | null;
  torihikisaki_post: string | null;
  torihikisaki_address: string | null;
  customer_id: string | null;
  company_name: string | null;
  company_name_kana: string | null;
  match_reason: string | null;
  similarity_name: number | null;
  similarity_kana: number | null;
  selected: boolean;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  // Cloud SQL 側候補の連絡先・住所 (list_preview の LEFT JOIN で取得)
  candidate_tel: string | null;
  candidate_fax: string | null;
  candidate_email: string | null;
  candidate_post: string | null;
  candidate_address_main: string | null;
  candidate_address_second: string | null;
}

export interface PreviewSummary {
  fuzz_threshold: number;
  deleted_old_rows: number;
  total_turquoise: number;
  hojin_kbn_counts: Record<string, number>;
  confirmed_skipped: number;
  skipped_excluded: number;
  target_count: number;
  corporate_candidate_count: number;
  individual_candidate_count: number;
  tier_counts: Record<MatchTier, number>;
  inserted_rows: number;
  elapsed_seconds: number;
}

export interface CommitCounts {
  corporate_updated: number;
  corporate_inserted: number;
  committed: number;
  rejected: number;
  skipped_duplicate: number;
  skipped_blank_name: number;
}

const PREVIEW_RUN_URL = ep('run_turquoise_match_preview');
const PREVIEW_LIST_URL = ep('list_turquoise_match_preview');
const PREVIEW_PATCH_URL = ep('patch_turquoise_match_selected');
const PREVIEW_PATCH_SKIP_URL = ep('patch_turquoise_match_skip');
const PREVIEW_PATCH_BULK_URL = ep('patch_turquoise_match_bulk_selected');
const PREVIEW_COMMIT_URL = ep('commit_turquoise_match_run');

export const useTurquoiseMatchPreview = () => {
  const [summary, setSummary] = useState<PreviewSummary | null>(null);
  const [hasData, setHasData] = useState(false);

  const [tab, setTab] = useState<TabKey>('unmatched');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  // フリーワード検索 (確定時のみ更新される。入力中の値はページ側のローカル state で保持)
  const [keyword, setKeywordState] = useState('');
  // 詳細検索条件 (旧画面 tq_partner_list.js の検索パネル相当。検索ボタン押下時のみ更新)
  const [filters, setFiltersState] = useState<MatchSearchFilters>(EMPTY_FILTERS);

  const [rows, setRows] = useState<MatchRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tierSummary, setTierSummary] = useState<Record<string, Record<string, number>>>({});

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runPreview = useCallback(async (fuzzThreshold: number = 85) => {
    setLoadingPreview(true);
    setError(null);
    try {
      const res = await axios.post(PREVIEW_RUN_URL, { fuzz_threshold: fuzzThreshold }, {
        timeout: 540000,
      });
      if (res.data?.status === 'success' && res.data.summary) {
        setSummary(res.data.summary);
        setHasData(true);
        setTab('unmatched');
        setPage(0);
        setKeywordState('');
        setFiltersState(EMPTY_FILTERS);
        return res.data.summary as PreviewSummary;
      }
      setError(res.data?.message || 'プレビュー実行に失敗しました');
      return null;
    } catch (err: any) {
      console.error('runPreview error', err);
      setError(err.response?.data?.message || err.message || 'プレビュー実行に失敗しました');
      return null;
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    if (!hasData) return;
    setLoadingList(true);
    setError(null);
    try {
      const isCommittedTab = tab === 'committed';
      const isSkippedTab = tab === 'skipped';
      const isHistoryTab = isCommittedTab || isSkippedTab;
      const statusParam = isCommittedTab ? 'committed' : isSkippedTab ? 'skipped' : 'pending';
      const res = await axios.post(PREVIEW_LIST_URL, {
        tier: isHistoryTab ? null : tab,
        status: statusParam,
        page,
        pageSize,
        keyword: keyword || null,
        filters,
      });
      if (res.data?.status === 'success') {
        setRows(res.data.items || []);
        setTotalCount(res.data.total || 0);
        setTierSummary(res.data.tierSummary || {});
      } else {
        setError(res.data?.message || '一覧取得に失敗しました');
      }
    } catch (err: any) {
      console.error('fetchList error', err);
      setError(err.response?.data?.message || err.message || '一覧取得に失敗しました');
    } finally {
      setLoadingList(false);
    }
  }, [hasData, tab, page, pageSize, keyword, filters]);

  const patchSelected = useCallback(async (matchId: number, selected: boolean) => {
    setRows((prev) =>
      prev.map((r) => (r.match_id === matchId ? { ...r, selected } : r))
    );
    try {
      await axios.post(PREVIEW_PATCH_URL, { match_id: matchId, selected });
    } catch (err: any) {
      console.error('patchSelected error', err);
      setRows((prev) =>
        prev.map((r) => (r.match_id === matchId ? { ...r, selected: !selected } : r))
      );
      setError(err.response?.data?.message || err.message || '個別更新に失敗しました');
    }
  }, []);

  const patchSkipped = useCallback(async (matchId: number, skippedBy?: string) => {
    // 楽観的更新 - 当該行を一覧から即座に取り除き、件数も減らす
    let removedRow: MatchRow | undefined;
    setRows((prev) => {
      removedRow = prev.find((r) => r.match_id === matchId);
      return prev.filter((r) => r.match_id !== matchId);
    });
    setTotalCount((prev) => Math.max(0, prev - 1));
    try {
      await axios.post(PREVIEW_PATCH_SKIP_URL, {
        match_id: matchId,
        skipped_by: skippedBy,
      });
      // tierSummary を最新化するため一覧を再取得 (見送りタブのカウントを正確に反映)
      await fetchList();
    } catch (err: any) {
      console.error('patchSkipped error', err);
      // ロールバック
      if (removedRow) {
        setRows((prev) => [...prev, removedRow as MatchRow]);
        setTotalCount((prev) => prev + 1);
      }
      setError(err.response?.data?.message || err.message || '見送り処理に失敗しました');
    }
  }, [fetchList]);

  const patchBulkSelected = useCallback(
    async (selected: boolean, tierFilter?: MatchTier | null) => {
      setLoadingList(true);
      setError(null);
      try {
        await axios.post(PREVIEW_PATCH_BULK_URL, {
          selected,
          tier: tierFilter ?? null,
        });
        await fetchList();
      } catch (err: any) {
        console.error('patchBulkSelected error', err);
        setError(err.response?.data?.message || err.message || '一括更新に失敗しました');
        setLoadingList(false);
      }
    },
    [fetchList]
  );

  const commitRun = useCallback(async (reviewedBy?: string): Promise<CommitCounts | null> => {
    setCommitting(true);
    setError(null);
    try {
      const res = await axios.post(PREVIEW_COMMIT_URL, { reviewed_by: reviewedBy }, {
        timeout: 300000,
      });
      if (res.data?.status === 'success') {
        return res.data.counts as CommitCounts;
      }
      setError(res.data?.message || '確定実行に失敗しました');
      return null;
    } catch (err: any) {
      console.error('commitRun error', err);
      setError(err.response?.data?.message || err.message || '確定実行に失敗しました');
      return null;
    } finally {
      setCommitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setSummary(null);
    setHasData(false);
    setRows([]);
    setTotalCount(0);
    setTierSummary({});
    setTab('unmatched');
    setPage(0);
    setKeywordState('');
    setFiltersState(EMPTY_FILTERS);
    setError(null);
  }, []);

  return {
    summary,
    hasData,
    tab,
    page,
    pageSize,
    keyword,
    filters,
    rows,
    totalCount,
    tierSummary,
    loadingPreview,
    loadingList,
    committing,
    error,
    setTab: (t: TabKey) => {
      setTab(t);
      setPage(0);
    },
    setPage,
    setPageSize: (s: number) => {
      setPageSize(s);
      setPage(0);
    },
    setFilters: (f: MatchSearchFilters) => {
      setFiltersState(f);
      setPage(0);
    },
    setKeyword: (k: string) => {
      setKeywordState(k);
      setPage(0);
    },
    runPreview,
    fetchList,
    patchSelected,
    patchSkipped,
    patchBulkSelected,
    commitRun,
    reset,
  };
};
